import React, { useContext, useEffect, useState } from 'react';
import DataSettings from './data-settings';
import Heading from '../../components/shared/heading';
import Loader from '../../components/shared/loader';
import { AccountsContext } from '../../components/context/account-context';
import { getAllNotifications, getAllNotificationTypes } from '../../services/notifications-service';
import ModalHeading from '../../components/shared/modal-heading';
import Button from '../../components/shared/custom-btn';
import { getAccounts, updateAccount } from '../../services/auth-service';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';
import Modal from '../../components/shared/modal';
import AuditLogModal from '../../components/shared/AuditLogModal';
import auditicon from '../../assets/images/audit-icon.png';

const Notifications = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [loading, setLoading] = useState(false);
    const [notificationTypes, setNotificationTypes] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [error, setError] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });
    const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);

    const fetchPreferences = async () => {
        setLoading(true);
        try {
            const [notificationTypesRes, notificationRes, accountRes] = await Promise.all([
                getAllNotificationTypes(accountId),
                getAllNotifications(accountId),
                getAccounts(),
            ]);

            if (notificationTypesRes.success) {
                setNotificationTypes(notificationTypesRes.data.data || []);
            } else {
                throw new Error(notificationTypesRes.data?.message || 'Failed to fetch notification types');
            }

            if (notificationRes.success) {
                setNotifications(notificationRes.data.data || []);
            } else {
                throw new Error(notificationRes.data?.message || 'Failed to fetch notifications');
            }

            if (accountRes.success) {
                setAccounts(accountRes.data.data || []);
            } else {
                throw new Error(accountRes.data?.message || 'Failed to fetch business accounts');
            }

            setError(null);
        } catch (err) {
            setError(err.message || 'An error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accountId) {
            fetchPreferences();
        }
    }, [accountId]);

    const handleToggle = async (key, currentValue) => {
        setLoading(true);
        setApiResponse({ message: '', type: '' });

        // clone the attributes from current account
        const updatedAttributes = {
            ...attributes,
            [key]: !currentValue,
        };

        const payload = { attributes: updatedAttributes };

        try {
            const response = await updateAccount(accountId, payload);
            if (response.success) {
                setApiResponse({ message: 'Account updated successfully!', type: 'success' });

                setAccounts((prev) =>
                    prev.map((acc, idx) =>
                        idx === 0 ? { ...acc, attributes: updatedAttributes } : acc
                    )
                );
            } else {
                setApiResponse({ message: response.data.message || 'Failed to update Account.', type: 'error' });
            }
        } catch (error) {
            setApiResponse({ message: 'An unexpected error occurred.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };




    const attributes = accounts[0]?.attributes || {};
    const { notifyAdmin, notifyManager } = attributes;
    console.log(attributes)

    useEffect(() => {
        if (apiResponse.message) {
            const timer = setTimeout(() => {
                setApiResponse({ message: '', type: '' });
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [apiResponse]);

    return (
        <div className="py-4">
            <div className='flex items-center flex-wrap justify-between gap-4 pb-3 px-6 border-b'>
                <div>
                    <Heading>Notifications</Heading>
                    <p className="text-gray-700">Manage account level notifications</p>
                </div>

                <div className='flex items-center gap-3'>
                    <ModalHeading>Notify</ModalHeading>

                    <Button
                        label='Admin'
                        onClick={() => handleToggle('notifyAdmin', notifyAdmin)}
                        className={`${notifyAdmin ? 'bg-purple-600 text-white' : 'bg-gray-100 text-black'} border-gray-400 px-3 !py-2 !text-sm rounded-lg`}
                    />
                    <Button
                        label='Manager'
                        onClick={() => handleToggle('notifyManager', notifyManager)}
                        className={`${notifyManager ? 'bg-purple-600 text-white' : 'bg-gray-100 text-black'} border-gray-400 px-3 !py-2 !text-sm rounded-lg`}
                    />
                    <button
                        className={`bg-gray-100 text-black border-gray-400 px-3 !py-1.5 !text-sm rounded-lg hover:scale-105 duration-300 transition-all`}
                        onClick={() => setIsAuditLogModalOpen(true)}>
                        <img src={auditicon} className='w-6 h-6 object-contain' alt="audit icon" />
                    </button>

                </div>
            </div>

            <div className="px-6">
                {apiResponse.message && (
                    <div
                        className={`mb-4 p-3 rounded ${apiResponse.type === 'success'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                            }`}
                    >
                        {apiResponse.message}
                    </div>
                )}

                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md mb-4">
                        {error}
                    </div>
                )}

                {loading ? (
                    <Loader />
                ) : (
                    <DataSettings
                        data={notifications}
                        attributes={attributes}
                        notificationTypes={notificationTypes}
                        accountId={accountId}
                    />
                )}
            </div>

            <Modal
                isOpen={isAuditLogModalOpen}
                onClose={() => setIsAuditLogModalOpen(false)}
                modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
            >
                <AuditLogModal
                    isOpen={isAuditLogModalOpen}
                    onClose={() => setIsAuditLogModalOpen(false)}
                    accountId={accountId}
                    linkedType="notification"
                    linkedId={accountId}
                />
            </Modal>
        </div>
    );
};

export default Notifications;
